import { AvTabState, type PubsubVirtualUserState } from "../../shared/types/av-state";

import { createAppSelector } from "./base";
import { selectActiveClassSessionId, selectIsClassSessionVirtual } from "./class-session";
import { selectCurrentClassUserId } from "./class-user";

import { type RootState } from "store";
import { stringArrayMemoizeOptions } from "store/selector-memoize";
import { objectEqualityFn } from "store/store-hooks";
import { filterMap } from "utils/collections";

const MAX_RECENT_SPEAKERS = 5;
const EMPTY_STRING_ARRAY: string[] = [];

// Like useObjectSelector: runtime still returns the full users map, but the type is narrowed
// to the given fields. resultEqualityCheck keeps a stable reference when those fields (and the
// user-id set) are unchanged, so RTT/connection churn does not invalidate downstream selectors.
export const createSelectVirtualUsersPick = <const K extends keyof PubsubVirtualUserState>(
  fields: readonly K[],
) =>
  createAppSelector(
    [(state: RootState) => state.pubsub_virtual_class_state.users],
    // Type-only narrow — same pattern as useObjectSelector's Pick return type.
    (virtualUsers) => virtualUsers as Record<string, Pick<PubsubVirtualUserState, K>>,
    {
      memoizeOptions: {
        resultEqualityCheck: (
          left: Record<string, Pick<PubsubVirtualUserState, K>>,
          right: Record<string, Pick<PubsubVirtualUserState, K>>,
        ) => {
          const leftIds = Object.keys(left);
          if (leftIds.length !== Object.keys(right).length) {
            return false;
          }
          return leftIds.every((classUserId) =>
            objectEqualityFn(left[classUserId], right[classUserId], fields),
          );
        },
      },
    },
  );

const selectVirtualUsersForPresence = createSelectVirtualUsersPick(["isPresent"]);
const selectVirtualUsersForRecentSpeakers = createSelectVirtualUsersPick([
  "isPresent",
  "isSpeaking",
  "lastSpokeAt",
]);
const selectVirtualUsersForCurrentlySpeaking = createSelectVirtualUsersPick(["isSpeaking"]);
const selectVirtualUsersForMainAvTab = createSelectVirtualUsersPick(["activeWebsocketId"]);

// the selectMainAvTab selector is used to determine if the current tab is the main AV tab
// we do this by looking for the user that has the unique_user_id set in the pubsub_virtual_class_state
export const selectIsMainAvTab = createAppSelector(
  [selectVirtualUsersForMainAvTab, (state: RootState) => state.auth.websocket?.uniqueId],
  (virtualUsers, uniqueId) => {
    if (!uniqueId) {
      return false;
    }

    for (const classUserId in virtualUsers) {
      if (virtualUsers[classUserId]?.activeWebsocketId === uniqueId) {
        return true;
      }
    }

    return false;
  },
);

export const selectVirtualUserState = (
  state: RootState,
  classUserId: string | null,
): PubsubVirtualUserState | null =>
  classUserId ? (state.pubsub_virtual_class_state.users[classUserId] ?? null) : null;

export const selectAvTabState = (state: RootState) => {
  return state.pubsub_virtual_class_state.av_tab_state;
};

export const selectStableIsMainAvTab = (state: RootState) => {
  return state.pubsub_virtual_class_state.av_tab_state !== AvTabState.NotMain;
};

export const selectMaxParticipantBarSlots = (state: RootState) => {
  return state.pubsub_virtual_class_state.max_participant_bar_slots;
};

export const selectClassUserIdsPresentInClass = createAppSelector(
  [selectVirtualUsersForPresence],
  (virtualUsers) =>
    Object.keys(virtualUsers).filter((classUserId) => virtualUsers[classUserId]?.isPresent),
  stringArrayMemoizeOptions,
);

export const selectIsVirtualUserPresentInClass = createAppSelector(
  [selectVirtualUserState],
  (user) => user?.isPresent ?? false,
);

export const selectRecentSpeakerClassUserIds = createAppSelector(
  [selectVirtualUsersForRecentSpeakers],
  (virtualUsers) => {
    // Get all entries with lastSpokeAt - users speaking now will have lastSpokeAt set
    const sorted = filterMap(
      Object.entries(virtualUsers),
      ([, userState]) => Boolean(userState.lastSpokeAt && userState.isPresent),
      ([classUserId]) => classUserId,
    ).sort((classUserIdA, classUserIdB) => {
      const a = virtualUsers[classUserIdA];
      const b = virtualUsers[classUserIdB];
      // Only an explicit true is "speaking now". Sync snapshots omit isSpeaking, so a
      // missing flag must sort with false and fall through to lastSpokeAt.
      const aSpeaking = a.isSpeaking === true;
      const bSpeaking = b.isSpeaking === true;
      if (aSpeaking !== bSpeaking) {
        return aSpeaking ? -1 : 1;
      }

      // Then by lastSpokeAt desc
      return (b.lastSpokeAt ?? 0) - (a.lastSpokeAt ?? 0);
    });

    return sorted.slice(0, MAX_RECENT_SPEAKERS);
  },
  stringArrayMemoizeOptions,
);

export const selectClassStateLastUpdatedAt = (state: RootState) => {
  return state.pubsub_virtual_class_state.last_updated_at;
};

export const selectIsVirtualUserSpeaking = createAppSelector(
  [selectVirtualUserState],
  (user) => user?.isSpeaking ?? false,
);

export const selectIsPublishedAudioEnabled = createAppSelector(
  [selectVirtualUserState],
  (user) => user?.isAudioEnabled ?? false,
);

export const selectIsVideoSuspendedByBrowser = createAppSelector(
  [selectVirtualUserState],
  (user) => user?.isVideoSuspendedByBrowser ?? false,
);

export const selectVirtualUserMicError = createAppSelector(
  [selectVirtualUserState],
  (user) => user?.micError ?? null,
);

export const selectVirtualUserCameraError = createAppSelector(
  [selectVirtualUserState],
  (user) => user?.cameraError ?? null,
);

export const selectVirtualUserConnectionState = createAppSelector(
  [selectVirtualUserState],
  (user) => user?.connectionState ?? null,
);

export const selectVirtualUserPubsubPingRtt = createAppSelector(
  [selectVirtualUserState],
  (user) => user?.pubsubPingRtt ?? null,
);

export const selectVirtualUserShareDBPingRtt = createAppSelector(
  [selectVirtualUserState],
  (user) => user?.shareDBPingRtt ?? null,
);

export const selectIsObservingVirtualClass = createAppSelector(
  [
    (state) => state.pubsub_virtual_class_state.observing_session_id,
    selectActiveClassSessionId,
    selectIsClassSessionVirtual,
  ],
  (observingVirtualClassId, currentClassSessionId, isVirtual) =>
    isVirtual && observingVirtualClassId === currentClassSessionId,
);

export const selectObserverListenedBreakoutGroupId = (state: RootState) => {
  return state.pubsub_virtual_class_state.observer_listened_breakout_group_id;
};

export const selectObserverBreakoutGroupId = (state: RootState) => {
  return state.pubsub_virtual_class_state.observer_breakout_group_id;
};

export const selectParticipantBarVisibleClassUserIds = (state: RootState) => {
  return state.pubsub_virtual_class_state.participant_bar_visible_class_user_ids;
};

export const selectIsParticipantBarVisibleClassUserId = createAppSelector(
  [
    selectParticipantBarVisibleClassUserIds,
    (_state, classUserId: string | null | undefined) => classUserId,
  ],
  (visibleClassUserIds, classUserId) =>
    Boolean(classUserId && visibleClassUserIds.includes(classUserId)),
);

export const selectPinnedClassUserIds = (state: RootState) => {
  return state.pubsub_virtual_class_state.pinned_class_user_ids ?? EMPTY_STRING_ARRAY;
};

export const selectIsPinnedClassUserId = createAppSelector(
  [selectPinnedClassUserIds, (state, classUserId: string) => classUserId],
  (pinnedClassUserIds, classUserId) => {
    return pinnedClassUserIds.includes(classUserId);
  },
);

export const selectCurrentlySpeakingClassUserIds = createAppSelector(
  [selectVirtualUsersForCurrentlySpeaking],
  (virtualUsers) =>
    Object.keys(virtualUsers).filter((classUserId) => virtualUsers[classUserId]?.isSpeaking),
  stringArrayMemoizeOptions,
);

export const selectAvActiveClassUserIds = (state: RootState) => {
  return state.pubsub_virtual_class_state.av_active_class_user_ids ?? EMPTY_STRING_ARRAY;
};

export const selectStateLeaderClassUserId = (state: RootState) => {
  return state.pubsub_virtual_class_state.leader_class_user_id ?? null;
};

export const selectIsStateParticipantBarLeader = createAppSelector(
  [selectCurrentClassUserId, selectStateLeaderClassUserId],
  (currentClassUserId, stateLeaderClassUserId) =>
    !!currentClassUserId && currentClassUserId === stateLeaderClassUserId,
);
